import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { AccordionBody, AccordionHeader, AccordionItem, Badge, Button, Input, Label, UncontrolledAccordion } from 'reactstrap'
import { useDebouncedCallback } from 'use-debounce'
import activities from '../../api/activities/hooks'
import agents from '../../api/agents/hooks'
import auth from '../../api/auth/hooks'
import { formatContactFieldOptions } from '../../api/contacts/formatters'
import { CONTACT_TYPE, DETAIL_VIEW, MODAL_TYPES } from '../../common/constants'
import { activityFields } from '../../common/forms/activities/fields'
import { activityResponseFields } from '../../common/response/activity'
import useHasBeenVisible from '../../hooks/useHasBeenVisible'
import { selectAccordionPagination, setAccordionPage } from '../../store/accordionPagination'
import { showModal } from '../../store/modal'
import { formatDateTime, getUrlBase } from '../../utils/general'
import DropDownWithCheckBox from '../ActivitiesViewAccordion/DropDownWithCheckBox/DropDownWithCheckBox'
import AppPermissionsSwitcher from '../AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../AppPermissionsSwitcher/constants/actions'
import { useEntityParams } from '../AppPermissionsSwitcher/EntityParamsContext'
import SkeletonAccordion from '../Common/Skeleton/SkeletonAccordion'
import { createActivityDateTime, setActivityModalData } from '../Modals/ActivityModal/utils'
import { setDeleteCallback } from '../Modals/DeleteModal/utils'

const ActivitiesViewAccordion = ({ parentId, contactType = CONTACT_TYPE.PERSON, location }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { ACTIVITY_MODAL, DELETE_MODAL } = MODAL_TYPES

    const entityParams = useEntityParams()
    const moduleName = getUrlBase(location.pathname.split('/')[1])

    const { ref, hasBeenVisible } = useHasBeenVisible()

    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({
            id: data.id,
            userSettings: data.userSettings,
        }),
    })
    const { mutate: updateAgentSettings } = agents.UpdateCurrentAgentSettings.useMutation()

    const page = useSelector((state) => selectAccordionPagination(state, 'activities')).currentPage || 1

    const [activityCriteria, setActivityCriteria] = useState(
        currentAgent?.id && currentAgent?.userSettings?.[`${moduleName}_activityCriteria`]
            ? currentAgent.userSettings[`${moduleName}_activityCriteria`]
            : null
    )

    const { data: activityValues } = activities.FormValues.useQuery({ enabled: hasBeenVisible })
    const activityTypes = useMemo(() => activityValues?.type ?? [], [activityValues])
    const dropdownOptions = [...activityTypes, { value: 'system', label: t('accordion.activities.dropdown.systemActivities') }]

    const activitiesParams = useMemo(
        () => ({
            contactId: parentId,
            limit: 5,
            page: page,
            order: {
                dateFrom: 'desc',
                timeFrom: 'desc',
                id: 'desc',
            },
            criteria: activityCriteria,
        }),
        [parentId, page, activityCriteria]
    )

    const { data: activitiesResponse, isLoading: fetchingActivities } = activities.GetActivities.useQuery(activitiesParams, {
        enabled: !!activityCriteria && hasBeenVisible,
    })

    const activityItems = activitiesResponse?.items ?? []
    const pagination = activitiesResponse?.pagination ?? {}

    const { mutate: updateActivity } = activities.UpdateActivity.useMutation()
    const { mutate: deleteActivity } = activities.DeleteActivity.useMutation()

    // Effect to set default activity criteria in user settings
    useEffect(() => {
        if (`${moduleName}_activityCriteria` in currentAgent.userSettings || !activityTypes.length) return

        const defaultActivityCriteria = {
            activityType: activityTypes.map(({ value }) => value),
            source: 'system',
        }

        setActivityCriteria(defaultActivityCriteria)

        updateAgentSettings({
            agentId: currentAgent.id,
            userSettings: {
                ...currentAgent.userSettings,
                [`${moduleName}_activityCriteria`]: defaultActivityCriteria,
            },
        })
    }, [activityTypes, moduleName, currentAgent.userSettings, currentAgent.id, updateAgentSettings])

    // Effect to reset page to 1 when parentId changes (e.g. switching between contacts)
    useEffect(() => {
        dispatch(setAccordionPage('activities', 1))
    }, [parentId, contactType, dispatch])

    const debouncedUpdate = useDebouncedCallback((newCriteria) => {
        updateAgentSettings({
            agentId: currentAgent.id,
            userSettings: { ...currentAgent.userSettings, [`${moduleName}_activityCriteria`]: newCriteria },
        })
    }, 2000)

    const handleCriteriaChange = (newCriteria) => {
        setActivityCriteria(newCriteria)
        debouncedUpdate(newCriteria)
    }

    const handleCreate = () => {
        const newActivity = {
            ...createActivityDateTime(),
            // prefill CONTACT & CONTACT_COMPANY fields with contact data
            ...(contactType === CONTACT_TYPE.PERSON && {
                [activityResponseFields.CONTACT]: { id: parentId },
                [activityResponseFields.CONTACT_COMPANY]: formatContactFieldOptions(parentId, 'contactPersonCompany', entityParams).map((c) => ({
                    id: c.value,
                })),
            }),
            ...(contactType === CONTACT_TYPE.COMPANY && {
                [activityResponseFields.CONTACT_COMPANY]: [{ id: parentId }],
            }),
        }

        const payload = {
            modalType: ACTIVITY_MODAL,
            modalProps: {
                mode: 'create',
                event: newActivity,
                parentId: parentId,
                detailView: contactType === CONTACT_TYPE.COMPANY ? DETAIL_VIEW.COMPANY : DETAIL_VIEW.CONTACT,
                params: entityParams,
            },
        }

        dispatch(showModal(payload))
    }

    const handleEdit = (activityItem) => {
        const cleanedActivity = setActivityModalData(activityItem)

        const payload = {
            modalType: ACTIVITY_MODAL,
            modalProps: {
                mode: 'edit',
                event: cleanedActivity,
                parentId: parentId,
                detailView: contactType === CONTACT_TYPE.COMPANY ? DETAIL_VIEW.COMPANY : DETAIL_VIEW.CONTACT,
            },
        }

        dispatch(showModal(payload))
    }

    const handleDelete = (activityItem) => {
        const callbackId = 'delete-activity'
        const payload = {
            modalType: DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () =>
            deleteActivity(activityItem.id, {
                onSuccess: () => {
                    const isLastItemOnPage = activityItems.length === 1 && page > 1
                    if (isLastItemOnPage) {
                        dispatch(setAccordionPage('activities', page - 1))
                    }
                },
            })
        )
        dispatch(showModal(payload))
    }

    const handlePrevious = () => {
        dispatch(setAccordionPage('activities', page - 1))
    }

    const handleNext = () => {
        dispatch(setAccordionPage('activities', page + 1))
    }

    return (
        <div ref={ref}>
            <UncontrolledAccordion defaultOpen={['1']} stayOpen>
                <AccordionItem className="mb-4">
                    <div className="position-relative">
                        <AccordionHeader targetId="1">
                            <h5 className="card-title my-2">{t('app.common.activities')}</h5>
                        </AccordionHeader>
                        {/* content inside div can't be inside AccordionHeader - warning appears: <button> cannot appear as a descendant of <button>  */}
                        <div className="position-absolute d-flex align-items-center gap-2" style={{ top: '10px', left: '110px', zIndex: 20 }}>
                            <Button onClick={handleCreate} color="add" className="btn-label">
                                <i className="mdi mdi-plus label-icon align-middle fs-16 me-2"></i>
                                {t('app.common.addActivity')}
                            </Button>
                            <DropDownWithCheckBox
                                activityTypes={dropdownOptions}
                                activityCriteria={activityCriteria ?? { activityType: [] }}
                                handleCriteriaChange={handleCriteriaChange}
                            />
                            {pagination?.totalPages > 1 && (
                                <Badge color="info" className="text-white p-2 fs-12" style={{ cursor: 'auto' }}>
                                    {`${page} / ${pagination.totalPages}`}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <AccordionBody accordionId="1">
                        <div className="acitivity-timeline">
                            {fetchingActivities ? (
                                <SkeletonAccordion count={5} />
                            ) : (
                                activityItems.map((activitiyItem) => {
                                    let activityTypeLetter = 'A'
                                    let activityStyle = { background: activitiyItem?.activityType?.color, color: activitiyItem.textColor }
                                    if (activitiyItem.activityType) {
                                        activityTypeLetter = activitiyItem.activityType?.name?.charAt(0)?.toUpperCase()
                                    }

                                    if (activitiyItem.source && activitiyItem.source === 'system') {
                                        activityStyle = { background: '#727A80', color: '#fff' }
                                    }

                                    return (
                                        <div className="acitivity-item py-3 d-flex" key={activitiyItem.id}>
                                            <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                                                <div
                                                    className={`avatar-title ${
                                                        activitiyItem.source && activitiyItem.source !== 'system' ? 'text-primary' : ''
                                                    } rounded-circle`}
                                                    style={activityStyle}
                                                >
                                                    {' '}
                                                    {activityTypeLetter}{' '}
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <div className="d-flex align-items-center justify-content-between mb-4">
                                                    <div>
                                                        {activitiyItem.title ? (
                                                            activitiyItem.source === 'system' ? (
                                                                <h6 className="mb-1">{activitiyItem.activityType.name}</h6>
                                                            ) : (
                                                                <h6 className="mb-1">{activitiyItem.title}</h6>
                                                            )
                                                        ) : null}
                                                        {activitiyItem.dateFrom && (
                                                            <small className="mb-0 text-secondary">{formatDateTime(activitiyItem.dateFrom)}</small>
                                                        )}
                                                        {activitiyItem.timeFrom && (
                                                            <small className="mb-0 text-secondary"> | {activitiyItem.timeFrom}</small>
                                                        )}
                                                    </div>
                                                    {activitiyItem.source && activitiyItem.source === 'user' ? (
                                                        <ul className="list-inline hstack justify-content-center">
                                                            <AppPermissionsSwitcher
                                                                permission="ModulePermissions"
                                                                permissionName={'module.activity.edit'}
                                                                action={action.edit}
                                                                module={'activities'}
                                                                activityAgentId={activitiyItem.activityAgent?.[0]?.agent?.id}
                                                            >
                                                                {({ hasModulePermission }) =>
                                                                    hasModulePermission && (
                                                                        <>
                                                                            <li>
                                                                                <Button
                                                                                    title={t('button.edit')}
                                                                                    color="link"
                                                                                    className="text-edit"
                                                                                    onClick={() => handleEdit(activitiyItem)}
                                                                                >
                                                                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                                                                </Button>
                                                                            </li>
                                                                            <li>
                                                                                <Button
                                                                                    title={t('button.delete')}
                                                                                    color="link"
                                                                                    className="text-danger"
                                                                                    onClick={() => handleDelete(activitiyItem)}
                                                                                >
                                                                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                                                                </Button>
                                                                            </li>
                                                                        </>
                                                                    )
                                                                }
                                                            </AppPermissionsSwitcher>
                                                        </ul>
                                                    ) : null}
                                                </div>
                                                {activitiyItem.project || activitiyItem.property ? (
                                                    <div className="mb-3">
                                                        {activitiyItem.project && activitiyItem.project?.length
                                                            ? activitiyItem.project.map((projectItem) => (
                                                                  <Link
                                                                      key={projectItem.id}
                                                                      to={`/projects/${projectItem.id}`}
                                                                      className="btn btn-sm btn-outline-warning btn-contact-activity me-2"
                                                                  >
                                                                      ID-{projectItem?.internalAppId}
                                                                  </Link>
                                                              ))
                                                            : ''}
                                                        {activitiyItem.property && activitiyItem.property?.length
                                                            ? activitiyItem.property.map((propertyItem) => (
                                                                  <Link
                                                                      key={propertyItem.id}
                                                                      to={`/properties/${propertyItem?.id}`}
                                                                      className="btn btn-sm btn-outline-warning btn-contact-activity me-2"
                                                                  >
                                                                      ID-{propertyItem?.internalAppId}
                                                                  </Link>
                                                              ))
                                                            : ''}
                                                    </div>
                                                ) : (
                                                    ''
                                                )}
                                                {activitiyItem.notes && <p className="text-secondary mb-2">{activitiyItem.notes}</p>}
                                                <div className="mt-3 d-flex align-items-center">
                                                    {activitiyItem.source && activitiyItem.source === 'user' ? (
                                                        <div className="form-check me-3">
                                                            <Input
                                                                type="checkbox"
                                                                className="form-check-input activity-status-check-input"
                                                                id={activityFields.COMPLETED + activitiyItem.id}
                                                                name={activityFields.COMPLETED + activitiyItem.id}
                                                                value={activitiyItem.completed || ''}
                                                                onChange={(e) => {
                                                                    updateActivity({
                                                                        id: activitiyItem.id,
                                                                        completed: e.target.checked ? 1 : 0,
                                                                    })
                                                                }}
                                                                checked={activitiyItem?.completed}
                                                            />
                                                            <Label className="form-check-label" for={activityFields.COMPLETED + activitiyItem.id}>
                                                                {activitiyItem?.completed
                                                                    ? t('modal.activity.field.completed')
                                                                    : t('modal.activity.field.planned')}{' '}
                                                            </Label>
                                                        </div>
                                                    ) : null}

                                                    {activitiyItem.activityAgent?.length
                                                        ? activitiyItem.activityAgent.map((acAgent, index) => (
                                                              <small className="text-secondary" key={acAgent.id}>
                                                                  {acAgent.agent?.firstName ?? ''} {acAgent.agent?.lastName ?? ''}
                                                                  {activitiyItem.activityAgent?.length - 1 !== index ? ' ' : ''}
                                                              </small>
                                                          ))
                                                        : ''}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            )}
                        </div>
                        {!fetchingActivities && (
                            <div className="d-flex justify-content-end p-2">
                                {page > 1 && (
                                    <div>
                                        <Button onClick={handlePrevious} color="secondary" className="btn btn-label me-3">
                                            <i className="mdi mdi-chevron-left label-icon me-2" />
                                            {t('button.navigationAction.previous')}
                                        </Button>
                                    </div>
                                )}
                                {page < pagination?.totalPages && (
                                    <div>
                                        <Button onClick={handleNext} color="secondary" className="btn-label right">
                                            <i className="mdi mdi-chevron-right label-icon" />
                                            {t('button.navigationAction.next')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </AccordionBody>
                </AccordionItem>
            </UncontrolledAccordion>
        </div>
    )
}

export default ActivitiesViewAccordion
